import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Components
import { Grid, Typography, Container, Paper, Divider, Icon } from "@mui/material";
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import { Button, Select, Tooltip } from "@gpstrackit/react-controls";
import { NavLink } from "react-router-dom";
import NewMigration from "Modules/importAccount/views/main/.components/newMigration";
import ExceptionDetails from "Modules/importAccount/views/main/.components/exceptionDetails";
import ShowRawData from "Modules/rawData/.components/showRawData";

//Helpers
import { getDateFormatted, getBadget, getImportAccountStatusOptions, isJson } from 'Core/data/Helpers';
import { HasPermission } from "Core/security";
import { endpoints } from 'Core/defaultValues';

//Hooks
import { useTokenOneTimeHandler } from 'Core/hooks';

//Actions
import {
    importAccountOpenModal,
    exceptionImportDetailsOpenModal,
    searchEntityRequest,
    showRawDataModal
} from 'Redux/actions';


const ImportAccountMain = (props) => {
    const { messages } = useIntl();
    const tableRef = useRef(null);
    const dispatch = useDispatch();

    //Redux Store
    const user = useSelector(state => state.securityRedux.user);
    const dealers = useSelector(state => state.generalRedux.dealers);
    const sources = useSelector(state => state.generalRedux.sources);
    const openModalNewMigration = useSelector(state => state.newMigrationRedux.openModal);
    const lang = useSelector(state => state.commonRedux.language);
    const tokenOneTime = useSelector(state => state.accountOverviewRedux.tokenOneTime);

    const { handleAuthenticate } = useTokenOneTimeHandler(tokenOneTime, dealers);

    //local states
    const [total, setTotal] = useState(0);
    const [source, setSource] = useState(null);
    const [status, setStatus] = useState(null);
    const [columns, setColumns] = useState([
        {
            key: "id",
            title: messages['import_account_internal_account_id'],
            width: 180,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_internal_account_id",
            functionString: 'return row?.targetAccountId',
            render: (item) => {
                return (
                    <span>{item.targetAccountId}</span>
                )
            }
        },
        {
            key: "sourceEntityId",
            title: messages['import_account_external_id'],
            width: 150,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_external_id",
            functionString: 'return row?.sourceEntityId',
            render: (item) => {
                return (
                    <span>{item.sourceEntityId}</span>
                )
            }
        },
        {
            key: "source",
            field: "source",
            title: messages['import_account_source'],
            width: 100,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_source",
            functionString: 'return row?.sourceName?.replace("_PX", "")',
            render: (item) => {
                return (
                    <div style={{ textAlign: 'center' }}>{item?.sourceName?.replace("_PX", "")}</div>
                )
            }
        },
        {
            key: "company_name",
            field: "company { id name sm username }",
            title: messages['import_account_label'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_label",
            functionString: 'return row?.company?.name',
            render: (item) => {
                let name = item?.company?.name
                if (name?.length >= 7) {
                    name = name?.substring(0, 40) + "...";
                }
                return (
                    <NavLink to={`/app/accounts/overview/${item?.company?.id}`} style={{ color: 'black' }}>
                        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{name}</span>
                    </NavLink>
                )
            }
        },
        {
            key: "status",
            field: "status",
            title: messages['import_account_status'],
            width: 100,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_status",
            functionString: 'return row?.status',
            render: (item) => {
                let color = ''
                switch (item?.status) {
                    case 'Done':
                        color = '#28a745';
                        break;
                    case 'DoneWithErrors':
                        color = '#ffc107';
                        break;
                    case 'InProgress':
                        color = '#17a2b8';
                        break;
                    case 'Pending':
                        color = '#6c757d';
                        break;
                    case 'Failed':
                        color = '#dc3545';
                        break;
                    default:
                        color = '#6c757d';
                }
                return (
                    <div style={{ textAlign: 'center' }}>{getBadget(messages[`import_account_${item?.status}`], true, null, color)}</div>
                )
            }
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['import_account_imported_on'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_imported_on",
            functionString: 'return row?.createdOn',
            render: (item) => {
                return (
                    <span>{item?.createdOn ? getDateFormatted(item?.createdOn, "date", "datetime", null, item?.timezone) : ''}</span>
                )
            }
        },
        {
            key: "updatedOn",
            field: "updatedOn",
            title: messages['import_account_updated_on'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_updated_on",
            functionString: 'return row?.updatedOn',
            render: (item) => {
                return (
                    <span>{item?.updatedOn ? getDateFormatted(item?.updatedOn, "date", "datetime", null, item?.timezone) : ''}</span>
                )
            }
        },
        {
            key: "resultsUpdatedOn",
            field: "resultsUpdatedOn",
            title: messages['import_account_results_updated_on'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_results_updated_on",
            functionString: 'return row?.resultsUpdatedOn',
            render: (item) => {
                return (
                    <span>{item?.resultsUpdatedOn ? getDateFormatted(item?.resultsUpdatedOn, "date", "datetime", null, item?.timezone) : ''}</span>
                )
            }
        },
        {
            key: "contacts",
            field: "contacts",
            title: messages['import_account_contacts'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_contacts",
            functionString: 'return row?.contacts',
            render: (item) => {
                return (
                    <div style={{ textAlign: 'center' }}>{item?.contacts || '0'}</div>
                )
            }
        },
        {
            key: "alerts",
            field: "alerts",
            title: messages['import_account_alerts'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_alerts",
            functionString: 'return row?.alerts',
            render: (item) => {
                return (
                    <div style={{ textAlign: 'center' }}>{item?.alerts || '0'}</div>
                )
            }
        },
        {
            key: "categories",
            field: "categories",
            title: messages['import_account_categories'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_categories",
            functionString: 'return row?.categories',
            render: (item) => {
                return (
                    <div style={{ textAlign: 'center' }}>{item?.categories || '0'}</div>
                )
            }
        },
        {
            key: "behavior",
            field: "behavior",
            title: messages['import_account_behavior'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_behavior",
            functionString: 'return row?.behavior',
            render: (item) => {
                return (
                    <div style={{ textAlign: 'center' }}>{item?.behavior || '0'}</div>
                )
            }
        },
        {
            key: "users",
            field: "users",
            title: messages['import_account_users'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_users",
            functionString: `
                    let resultPipelines = row?.results ? JSON.parse(row?.results)?.pipelines : null;
                    let users = resultPipelines?.find((pipeline) => pipeline.entity === "user")?.loaded || row?.users || '0';
                    return users
            `,
            render: (item) => {
                let resultPipelines = item?.results ? JSON.parse(item?.results)?.pipelines : null;
                let users = resultPipelines?.find((pipeline) => pipeline.entity === "user")?.loaded || item?.users || '0';
                return (
                    <div style={{ textAlign: 'center' }}>{users}</div>
                )
            }
        },
        {
            key: "drivers",
            field: "drivers",
            title: messages['import_account_drivers'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_drivers",
            functionString: `
                let resultPipelines = row?.results ? JSON.parse(row?.results)?.pipelines : null;
                let drivers = resultPipelines?.find((pipeline) => pipeline.entity === "driver")?.loaded || row?.drivers || '0';
                return drivers
            `,
            render: (item) => {
                let resultPipelines = item?.results ? JSON.parse(item?.results)?.pipelines : null;
                let drivers = resultPipelines?.find((pipeline) => pipeline.entity === "driver")?.loaded || item?.drivers || '0';
                return (
                    <div style={{ textAlign: 'center' }}>{drivers}</div>
                )
            }
        },
        {
            key: "devices",
            field: "devices",
            title: messages['import_account_devices'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_devices",
            functionString: 'return row?.devices',
            render: (item) => {
                return (
                    <div style={{ textAlign: 'center' }}>{item?.devices || '0'}</div>
                )
            }
        },
        {
            key: "assets",
            title: messages['import_account_assets'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_assets",
            functionString: `
                    let resultPipelines = row?.results ? JSON.parse(row?.results)?.pipelines : null;
                    let assets = resultPipelines?.find((pipeline) => pipeline.entity === "asset")?.loaded || row?.assets || '0';
                    return assets >= 0 ? assets : '0';
            `,
            render: (item) => {
                let resultPipelines = item?.results ? JSON.parse(item?.results)?.pipelines : null;
                let assets = resultPipelines?.find((pipeline) => pipeline.entity === "asset")?.loaded || 0;
                return (
                    <div style={{ textAlign: 'center' }}>{assets >= 0 ? assets : '0'}</div>
                )
            }
        },
        {
            key: "geofences",
            field: "geofences",
            title: messages['import_account_geofences'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_geofences",
            functionString: 'return row?.geofences',
            render: (item) => {
                return (
                    <div style={{ textAlign: 'center' }}>{item?.geofences || '0'}</div>
                )
            }
        },
        {
            key: "maintenance_tasks",
            field: "maintenance_tasks",
            title: messages['import_account_maintenance'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_maintenance",
            functionString: 'return row?.maintenance_tasks',
            render: (item) => {
                return (
                    <div style={{ textAlign: 'center' }}>{item?.maintenance_tasks || '0'}</div>
                )
            }
        },
        {
            key: "road_speed_overrides",
            field: "road_speed_overrides",
            title: messages['import_account_speed_override'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_speed_override",
            functionString: 'return row?.road_speed_overrides',
            render: (item) => {
                return (
                    <div style={{ textAlign: 'center' }}>{item?.road_speed_overrides || '0'}</div>
                )
            }
        },
        {
            key: "exceptions",
            field: "exceptions",
            title: messages['import_account_exceptions'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_exceptions",
            functionString: 'return row?.exceptions',
            render: (item) => {
                return (
                    <div style={{ textAlign: 'center' }}>
                        <a
                            style={{
                                textDecoration: item?.exceptions && 'underline',
                                cursor: item?.exceptions && 'pointer'
                            }}
                            onClick={() => {
                                item?.exceptions && dispatch(exceptionImportDetailsOpenModal(true, item))
                            }}
                        >
                            {item?.exceptions || '0'}
                        </a>
                    </div>
                )
            }
        },
    ]);
    const [searchFields, setSearchFields] = useState(["company_id", "sourceEntityId", "company_name", "source"]);
    const [rawData, setRawData] = useState("");

    const showModal = (raw) => {
        setRawData(raw);
        dispatch(showRawDataModal())
    }

    useEffect(() => {
        (!dealers || dealers.length === 0) && dispatch(searchEntityRequest({ key: "dealers" }));
        (!sources || sources.length === 0) && dispatch(searchEntityRequest({ key: "sources", fields: ["entityName"], terms: ["importProcess"] }));
    }, []);

    useEffect(() => {

        if (status)
            setStatus({ id: status.id, label: messages[`import_account_${status.id}`] });

        let currentColumns = columns.map((item) => ({
            ...item,
            title: messages[item.code],
            render: item.key === "status" ? (item) => {
                let color = ''
                switch (item?.status) {
                    case 'Done':
                        color = '#28a745';
                        break;
                    case 'DoneWithErrors':
                        color = '#ffc107';
                        break;
                    case 'InProgress':
                        color = '#17a2b8';
                        break;
                    case 'Pending':
                        color = '#6c757d';
                        break;
                    case 'Failed':
                        color = '#dc3545';
                        break;
                    default:
                        color = '#6c757d';
                }
                return (
                    <div style={{ textAlign: 'center' }}>{getBadget(messages[`import_account_${item?.status}`], true, null, color)}</div>
                )
            } : item.render,
        }))
        setColumns(currentColumns)
    }, [lang])


    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    <Grid item xs={6}>
                        <Typography variant="h5" gutterBottom component="div">
                            {messages['import_account_title']} {/* ({total}) */}
                        </Typography>
                    </Grid>
                    <Grid item xs={6} className="main-view-buttons" style={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            className="btn-dark-blue"
                            onClick={() => {
                                dispatch(importAccountOpenModal(true))
                            }}
                            disabled={!HasPermission(user, "mgrtns", "c")}
                        >
                            {messages['import_account_import_account']}
                        </Button>
                    </Grid>

                    <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                        <Divider style={{ backgroundColor: '#999999' }} />
                    </div>
                    <DataViewInfiniteScroll
                        key={"pm_main_import_account"}
                        ref={tableRef}
                        name="pm-import-account-v2"
                        entity="import-account"
                        clearOnUnmount={false}
                        infiniteScroll
                        columns={columns}
                        graphqlConfig={{
                            function: "searchCompanyMigrations",
                            sortType: "CompanyMigrationSortInput",
                            conditionsType: "CompanyMigrationSearchInput",
                            customQuery: "id sourceName sourceEntityName sourceEntityId newEntityName  newEntityId targetAccountId company { id dealerId username email } results ",
                            dynamicQuery: true,
                        }}
                        searchConfig={{
                            noAutomatic: true,
                            conditions: ["company_id", "company_name", "sourceEntityId", "source"],
                            fields: searchFields,
                            optionValue: "company { id }",
                            optionLabel: "company { name }",
                            extraField: " id company { id name }",
                        }}
                        exportConfig={{
                            fileName: "importaccount",
                        }}
                        uiConfig={{
                            hideSplit: true,
                            topHeight: 200,
                            hideFilters: true,
                        }}
                        defaultsConfig={{
                            valuesPerPage: [10, 25, 50, 100],
                        }}
                        onChangeConditions={(tags, conditions, total, newColumns, rows, sort, key, fromPreferences, restoreOnly) => {
                            // restoreOnly: data already persisted in redux, just sync UI states — skip getData
                            if (restoreOnly) return;

                            //current conditions
                            let currentConditions = {}
                            let currentTags = {}
                            if (!conditions && tags) {
                                currentConditions = tableRef?.current?.conditions;
                                currentTags = tags
                            } else {
                                currentConditions = conditions || [];
                                currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
                                if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
                            }

                            currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'company_id');
                            let tagsById = currentTags.filter((item) => item.company);
                            if (tagsById?.length > 0) {
                                //New Items
                                let currentIds = tagsById.map((item) => (item.id));
                                if (currentIds.length > 0) {
                                    currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
                                }
                            }
                            let tagsByLabel = currentTags.filter((item) => !item.company);
                            if (tagsByLabel.length > 0) {
                                let currentLabels = tagsByLabel.map((item) => (item["company { name }"]))
                                if (currentLabels.length > 0) {
                                    currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
                                }
                            }

                            let validateStatus = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "status");
                            let currentStatus = getImportAccountStatusOptions(messages)?.find((option) => `${option.id}` === validateStatus?.terms[0]);
                            currentStatus && setStatus(currentStatus);

                            let validateSource = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "source");
                            let currentSource = sources?.find((option) => `${option.id}` === validateSource?.terms[0]);
                            currentSource && setSource(currentSource);

                            if (!currentConditions && tags) tableRef?.current?.getData({ newConditions: currentConditions });
                            else tableRef?.current?.getData({ newRows: rows, newSort: sort, newConditions: currentConditions, newColumns });


                        }}
                        onChangeTotal={(total) => setTotal(total)}
                        onChangeColumns={(items) => setColumns(items)}
                        tableButtons={['orderColumn', 'export']}
                        menuOptions={() => {
                            return [
                                messages['accounts_main_authenticate_cloud'],
                            ]
                        }}
                        onClickMenu={(event, item, eventClick) => {
                            if (event === messages['accounts_main_authenticate_cloud']) {
                                if (HasPermission(user, "cmplc")) {
                                    handleAuthenticate(eventClick, item?.company?.dealerId, item?.company?.email || item?.company?.username, `PX_${item?.company?.id}`);
                                } else {
                                    NotificationManager.warning(
                                        messages['common_error_permission'],
                                        messages['accounts_main_authenticate_cloud'],
                                        5000,
                                    );
                                }
                            }
                        }}
                        extraFilters={() => {
                            return (
                                <div style={{ display: "flex", justifyContent: 'flex-end', gap: '10px', flexWrap: 'nowrap', position: 'relative', zIndex: 5 }}>
                                    <div style={{ width: '200px', minWidth: '200px', flexShrink: 0 }}>
                                        <Select
                                            id="status-id"
                                            valueField="id"
                                            textField="label"
                                            value={status}
                                            placeholder={messages['import_account_status']}
                                            options={getImportAccountStatusOptions(messages) || []}
                                            onChange={(status) => {
                                                setStatus(status);
                                                let currentConditions = tableRef?.current?.conditions;
                                                let newConditions = currentConditions.filter((item) => item.fields[0] !== 'status');

                                                if (status && status.id !== null) {
                                                    newConditions.push({ fields: ["status"], terms: [`${status.id}`], exact: true })
                                                }

                                                tableRef?.current?.getData({ newConditions });
                                            }}
                                            isClearable
                                        />
                                    </div>
                                    <div style={{ width: '130px', minWidth: '100px', flexShrink: 0, marginRight: '10px' }}>
                                        <Select
                                            id="source-id"
                                            valueField="id"
                                            textField="label"
                                            value={source}
                                            placeholder={messages['import_account_source']}
                                            options={sources || []}
                                            onChange={(source) => {
                                                setSource(source);
                                                let currentConditions = tableRef?.current?.conditions;
                                                let newConditions = currentConditions.filter((item) => item.fields[0] !== 'source');

                                                if (source && source.id !== null) {
                                                    newConditions.push({ fields: ["source"], terms: [`${source.id}`], exact: true })
                                                }

                                                tableRef?.current?.getData({ newConditions });
                                            }}
                                            isClearable
                                        />
                                    </div>
                                </div>
                            )
                        }}
                        customColumn={{
                            render: (row) => {
                                let results = row?.results ? JSON.parse(row?.results) : null;
                                if (!results) return null

                                return (
                                    <div>
                                        <Tooltip title={messages['import_account_results']} placement="top">
                                            <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                                                showModal(results)
                                            }} >
                                                remove_red_eye
                                            </Icon>
                                        </Tooltip>
                                    </div>
                                )
                            }
                        }}
                    />
                </Grid>
            </Paper>
            <ShowRawData rawData={isJson(rawData) ? JSON.parse(rawData) : rawData} />
            {openModalNewMigration && <NewMigration getData={tableRef?.current?.getData} />}
            <ExceptionDetails />
        </Container>
    )
}
export default ImportAccountMain;